import { Download, Filter, Info } from 'lucide-react'
import type { ReactNode } from 'react'
import { useSearchParams } from 'react-router'
import { PageTitle } from '@/app/layout'
import { BarList, Donut, Funnel, HeatMap, HeatScale, Legend, StackedBars } from '@/components/redesign/charts'
import { sequential, sequentialInk } from '@/components/redesign/chart-utils'
import { CountryCode, FilterChip, KpiTile, Panel } from '@/components/redesign/bits'
import { Button } from '@/components/ui/button'
import { Segmented } from '@/components/ui/segmented'
import { Skeleton } from '@/components/ui/skeleton'
import { ErrorState } from '@/components/ui/states'
import { cn } from '@/lib/cn'
import { numberFmt } from '@/lib/format'
import {
  type AudienceFilters,
  type Channel,
  type Demographics,
  type DemographicsRange,
  type Market,
  useDemographics,
} from './api'

const RANGES: { value: DemographicsRange; label: string; text: string }[] = [
  { value: '30d', label: '30 days', text: 'last 30 days' },
  { value: '90d', label: '90 days', text: 'last 90 days' },
  { value: '12m', label: '12 months', text: 'last 12 months' },
  { value: 'all', label: 'All time', text: 'all time' },
]
const NINETY_DAYS = RANGES[1] ?? { value: '90d' as DemographicsRange, label: '90 days', text: 'last 90 days' }

/** Where people came from, in the chart colours the Briefing uses. */
const CHANNELS: Record<Channel, { label: string; color: string }> = {
  google: { label: 'Google Ads', color: 'var(--viz-1)' },
  meta: { label: 'Meta ads', color: 'var(--viz-2)' },
  referral: { label: 'Partner referral', color: 'var(--viz-3)' },
  tagged: { label: 'Other tagged links', color: 'var(--viz-4)' },
  untagged: { label: 'No campaign tags', color: 'var(--viz-other)' },
}
const CHANNEL_KEYS = Object.keys(CHANNELS) as Channel[]

const MARKETS: Record<Market, { label: string; color: string }> = {
  tier1: { label: 'Tier 1', color: 'var(--viz-1)' },
  hubs: { label: 'Hubs', color: 'var(--viz-2)' },
  other: { label: 'Elsewhere', color: 'var(--viz-other)' },
}
const TIER1 = ['US', 'GB', 'CA', 'AU', 'IE', 'NZ']
const HUBS = ['PH', 'ZA', 'SG', 'MY']
const marketOf = (code: string): Market =>
  TIER1.includes(code) ? 'tier1' : HUBS.includes(code) ? 'hubs' : 'other'

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const FILTER_KEYS = ['market', 'country', 'channel', 'plan', 'method'] as const

const pct = (v: number | null | undefined, digits = 0) =>
  v == null ? '—' : `${v.toFixed(digits).replace(/\.0+$/, '')}%`
const share = (part: number, whole: number) => (whole > 0 ? (part / whole) * 100 : 0)
const shortDate = (iso: string, bucket: 'week' | 'month') =>
  new Intl.DateTimeFormat(
    'en-US',
    bucket === 'week' ? { month: 'short', day: 'numeric' } : { month: 'short', year: '2-digit' },
  ).format(new Date(`${iso}T00:00:00`))

function Swatch({ color }: { color: string }) {
  return (
    <i style={{ width: 8, height: 8, borderRadius: 2, background: color, display: 'block', flex: 'none' }} />
  )
}

/**
 * Who uses Arva SEO (redesign of 2026-10-16): who signs up and from where, which markets and
 * campaigns turn into customers, when people sign up, their devices, who keeps coming back, what
 * they use and the websites they bring. Counts and shares only: nobody is named. The period and
 * filters live in the address, so a view can be shared.
 */
export function AudiencePage() {
  const [params, setParams] = useSearchParams()
  const picked = RANGES.find((r) => r.value === params.get('range')) ?? NINETY_DAYS
  const filters: AudienceFilters = Object.fromEntries(FILTER_KEYS.map((k) => [k, params.get(k)]))
  const data = useDemographics(picked.value, filters)
  const filtered = FILTER_KEYS.some((k) => params.get(k))

  const set = (key: string, value: string | null) => {
    const next = new URLSearchParams(params)
    if (value === null || (key === 'range' && value === '90d')) next.delete(key)
    else next.set(key, value)
    setParams(next, { replace: true })
  }

  return (
    <>
      <PageTitle
        title="Audience"
        description="Who signs up, where they are, how they found Arva SEO and what they go on to use. Counts only: nobody is named on this page."
        actions={
          <Button
            variant="secondary"
            disabled={!data.data}
            onClick={() => data.data && downloadCsv(data.data, picked.value)}
          >
            <Download aria-hidden="true" />
            Export CSV
          </Button>
        }
      />
      <div className="fbar">
        <Segmented
          label="Period"
          value={picked.value}
          onChange={(v) => set('range', v)}
          options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
        />
        <FilterChip
          label="Market"
          icon={<Filter aria-hidden="true" />}
          value={filters.market}
          onChange={(v) => set('market', v)}
          options={[
            { value: 'tier1', label: 'Tier 1' },
            { value: 'hubs', label: 'Hubs' },
            { value: 'other', label: 'Everywhere else' },
          ]}
        />
        <FilterChip
          label="Country"
          icon={<Filter aria-hidden="true" />}
          value={filters.country}
          onChange={(v) => set('country', v)}
          options={(data.data?.country_options ?? []).map((c) => ({
            value: c.value,
            label: c.label,
            count: `${numberFmt.format(c.count)} · ${numberFmt.format(c.paid)} paying`,
          }))}
        />
        <FilterChip
          label="Came from"
          icon={<Filter aria-hidden="true" />}
          value={filters.channel}
          onChange={(v) => set('channel', v)}
          options={CHANNEL_KEYS.map((k) => ({ value: k, label: CHANNELS[k].label }))}
        />
        <FilterChip
          label="Plan now"
          icon={<Filter aria-hidden="true" />}
          value={filters.plan}
          onChange={(v) => set('plan', v)}
          options={(data.data?.plans ?? []).map((p) => ({ value: p.key, label: p.name }))}
        />
        <FilterChip
          label="Signed up with"
          icon={<Filter aria-hidden="true" />}
          value={filters.method}
          onChange={(v) => set('method', v)}
          options={[
            { value: 'google', label: 'Google' },
            { value: 'email', label: 'Email and password' },
          ]}
        />
        {filtered && (
          <button
            type="button"
            className="linkbtn"
            onClick={() => {
              const next = new URLSearchParams(params)
              FILTER_KEYS.forEach((k) => next.delete(k))
              setParams(next, { replace: true })
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {data.isPending ? (
        <Skeleton className="h-96 rounded-[14px]" />
      ) : data.isError ? (
        <div className="card">
          <ErrorState error={data.error} onRetry={() => void data.refetch()} />
        </div>
      ) : (
        <Body d={data.data} rangeText={picked.text} dim={data.isPlaceholderData} />
      )}
    </>
  )
}

function Body({ d, rangeText, dim }: { d: Demographics; rangeText: string; dim: boolean }) {
  const t = d.totals
  const p = d.previous
  const compare = p ? `vs the previous ${rangeText.replace('last ', '')}` : undefined
  const signups = d.signups.slice(-12)
  const unit = d.bucket === 'week' ? 'week' : 'month'
  const stickiness = t.active_30d > 0 ? share(t.active_1d, t.active_30d) : null
  const signedUp = d.methods.google + d.methods.email

  return (
    <div className={cn('sec transition-opacity', dim && 'opacity-60')} style={{ gap: 20 }}>
      <div className="kpis">
        <KpiTile
          label="Users with open accounts"
          value={numberFmt.format(t.open_users)}
          now={t.open_users}
          previous={p?.open_users}
          compare={compare ?? `${numberFmt.format(t.workspaces)} workspaces`}
          spark={signups.map((s) => s.open)}
        />
        <KpiTile
          label="New sign-ups"
          value={numberFmt.format(t.new_users)}
          now={t.new_users}
          previous={p?.new_users}
          compare={compare ?? rangeText}
          spark={signups.map((s) => s.users)}
        />
        <KpiTile
          label="Active this week"
          value={numberFmt.format(t.active_7d)}
          compare={`${pct(share(t.active_7d, t.open_users))} of users`}
        />
        <KpiTile
          label="Daily of monthly active"
          value={pct(stickiness)}
          compare="how many come back daily"
          hint="People active in the last day, as a share of those active in the last 30 days"
        />
        <KpiTile
          label="Activated"
          value={pct(t.activation)}
          now={t.activation}
          previous={p?.activation}
          points
          compare={t.activation == null ? 'no workspaces a week old yet' : compare}
          spark={signups.map((s) => s.activation ?? 0)}
          hint="Verified a website and finished an audit within 7 days of signing up"
        />
        <KpiTile
          label="Work email"
          value={pct(t.work_email)}
          now={t.work_email}
          previous={p?.work_email}
          points
          compare="the rest use Gmail, Outlook and the like"
        />
      </div>

      <Panel
        title={`Sign-ups each ${unit}`}
        sub="Coloured by where each person came from"
        extra={<Legend keys={CHANNEL_KEYS.map((k) => CHANNELS[k])} />}
      >
        {t.new_users === 0 ? (
          <p className="empty-line">No sign-ups in this period.</p>
        ) : (
          <StackedBars
            rows={d.by_channel.map((r) => ({
              label: shortDate(r.start, d.bucket),
              values: Object.fromEntries(CHANNEL_KEYS.map((k) => [k, r[k]])),
            }))}
            keys={CHANNEL_KEYS.map((k) => ({ key: k, ...CHANNELS[k] }))}
            width={1100}
            height={250}
            label={`Sign-ups each ${unit} by where they came from`}
          />
        )}
        <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 12 }}>
          “No campaign tags” covers search, direct visits and everyone who signed up before October 16, 2026,
          when sign-ups started keeping their campaign.
        </p>
      </Panel>

      <div className="grid2">
        <Panel
          title="Where they are"
          sub="From the time zone their browser gave at sign-up. Beside each bar: how many became customers, and their share."
          extra={<Legend keys={Object.values(MARKETS)} />}
        >
          {d.countries.rows.length === 0 ? (
            <p className="empty-line">No sign-ups with a known country in this period.</p>
          ) : (
            <BarList
              rows={d.countries.rows.map((c) => ({
                key: c.value,
                label: c.label,
                value: c.count,
                sub: `${numberFmt.format(c.paid)} customer${c.paid === 1 ? '' : 's'} · ${pct(c.paid_share, 1)}`,
                lead: <CountryCode code={c.value} name={c.label} />,
                color: MARKETS[marketOf(c.value)].color,
              }))}
            />
          )}
          {d.countries.unknown > 0 && (
            <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 12 }}>
              {numberFmt.format(d.countries.unknown)} unknown: signed up before time zones were kept, or the
              browser didn’t give one.
            </p>
          )}
        </Panel>
        <div className="sec" style={{ gap: 16 }}>
          <div className="grid3" style={{ gap: 12 }}>
            {d.markets.map((m) => (
              <div key={m.key} className="mt" style={{ cursor: 'default' }}>
                <span className="l">
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <Swatch color={MARKETS[m.key].color} />
                    {MARKETS[m.key].label}
                  </span>
                </span>
                <span className="v">{numberFmt.format(m.count)}</span>
                <span className="cmp">
                  <b style={{ color: 'var(--ink)' }}>{pct(m.paid_share, 1)}</b> become customers
                </span>
              </div>
            ))}
          </div>
          <Panel title="Campaigns" sub="From the tags on the link they signed up through, since October 16">
            {d.campaigns.length === 0 ? (
              <p className="empty-line">No tagged sign-ups in this period.</p>
            ) : (
              <div className="tw" style={{ border: 0 }}>
                <table className="t" style={{ minWidth: 460 }}>
                  <thead>
                    <tr>
                      <th>Campaign</th>
                      <th className="r">Sign-ups</th>
                      <th className="r">Activated</th>
                      <th className="r">Paid</th>
                      <th className="r">To paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {d.campaigns.map((c) => (
                      <tr key={c.campaign}>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                            <Swatch color={CHANNELS[c.channel].color} />
                            <b>{c.campaign}</b>
                          </span>
                          <small>
                            {CHANNELS[c.channel].label}
                            {c.source && c.channel !== 'google' && c.channel !== 'meta'
                              ? ` · ${c.source}`
                              : ''}
                          </small>
                        </td>
                        <td className="r num">{numberFmt.format(c.signups)}</td>
                        <td className="r num">{pct(share(c.activated, c.signups))}</td>
                        <td className="r num">{numberFmt.format(c.paid)}</td>
                        <td className="r num">
                          <b>{pct(share(c.paid, c.signups), 1)}</b>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </div>
      </div>

      <div className="grid2">
        <Panel title="When they sign up" sub={`Day and hour in their own time zone, ${rangeText}`}>
          <HeatMap grid={d.heatmap} />
          <div className="bar" style={{ marginTop: 12, justifyContent: 'space-between' }}>
            <span style={{ fontSize: 12.5, color: 'var(--muted)' }}>{busiest(d.heatmap)}</span>
            <HeatScale />
          </div>
        </Panel>
        <Devices devices={d.devices} />
      </div>

      <Panel
        title="How far new workspaces get"
        sub="Workspaces made in the range, and the share that reached each step"
      >
        {(d.funnel[0]?.count ?? 0) > 0 ? (
          <Funnel steps={d.funnel} />
        ) : (
          <p className="empty-line">No new workspaces in this period.</p>
        )}
      </Panel>

      {d.coach && <Coach coach={d.coach} />}

      <Cohorts cohorts={d.cohorts} />

      <div className="grid2">
        <Panel
          title="What they use"
          sub="Share of the workspaces made in the range that have used each feature"
        >
          {d.adoption.length === 0 ? (
            <p className="empty-line">No new workspaces in this period.</p>
          ) : (
            <BarList
              rows={d.adoption.map((a) => ({ key: a.key, label: a.label, value: a.share }))}
              format={(v) => pct(v)}
              labelWidth={230}
              valueWidth={44}
            />
          )}
        </Panel>
        <div className="sec" style={{ gap: 16 }}>
          <Panel title="How they sign up">
            <div className="grid2" style={{ gap: 14 }}>
              {[
                { label: 'With Google', value: share(d.methods.google, signedUp), color: 'var(--viz-1)' },
                {
                  label: 'Email and password',
                  value: share(d.methods.email, signedUp),
                  color: 'var(--viz-4)',
                },
              ].map((m) => (
                <div key={m.label}>
                  <div style={{ fontFamily: 'var(--f-display)', fontSize: 28, fontWeight: 500 }}>
                    {signedUp > 0 ? pct(m.value) : '—'}
                  </div>
                  <div
                    style={{
                      fontSize: 12.5,
                      color: 'var(--subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <Swatch color={m.color} />
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
            <div className="srow" style={{ marginTop: 8 }}>
              <span>Through a partner’s referral link</span>
              <b>{signedUp > 0 ? pct(share(d.methods.referred, signedUp)) : '—'}</b>
            </div>
            <div className="srow">
              <span>Ticked the newsletter box</span>
              <b>{pct(t.marketing)}</b>
            </div>
          </Panel>
          <Panel title="The websites they bring" sub="Kind of website and the platform it runs on">
            <div className="grid2" style={{ gap: 20 }}>
              <ShareList title="Kind" rows={d.website_types} labelWidth={112} />
              <ShareList title="Platform" rows={d.platforms} labelWidth={84} />
            </div>
          </Panel>
        </div>
      </div>
    </div>
  )
}

/** A short list of shares (of everything listed) with a small heading. */
function ShareList({
  title,
  rows,
  labelWidth,
  color = 'var(--viz-2)',
}: {
  title: string
  rows: { label: string; count: number }[]
  labelWidth: number
  color?: string
}) {
  const total = rows.reduce((sum, r) => sum + r.count, 0)
  return (
    <div>
      <div className="pop-l" style={{ padding: '0 0 8px' }}>
        {title}
      </div>
      {total === 0 ? (
        <p className="empty-line">None yet.</p>
      ) : (
        <BarList
          rows={rows
            .slice(0, 6)
            .map((r) => ({ key: r.label, label: r.label, value: Math.round(share(r.count, total)), color }))}
          format={(v) => `${v}%`}
          labelWidth={labelWidth}
          valueWidth={38}
        />
      )}
    </div>
  )
}

function Devices({ devices }: { devices: Demographics['devices'] }) {
  const colors: Record<string, string> = {
    Computer: 'var(--viz-1)',
    Phone: 'var(--viz-2)',
    Tablet: 'var(--viz-4)',
  }
  const parts = devices.device.map((r) => ({
    label: r.label,
    value: Math.round(share(r.count, devices.known)),
    color: colors[r.label] ?? 'var(--viz-other)',
  }))
  const computer = parts.find((r) => r.label === 'Computer')?.value ?? 0
  return (
    <Panel title="Devices and browsers" sub="From each person’s latest sign-in">
      {devices.known === 0 ? (
        <p className="empty-line">No sign-ins on record for these people yet.</p>
      ) : (
        <>
          <div className="donut-wrap">
            <Donut parts={parts} label={`${computer}%`} sub="on a computer" />
            <div className="legend">
              {parts.map((r) => (
                <span key={r.label}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <i style={{ background: r.color }} />
                    {r.label}
                  </span>
                  <b>{r.value}%</b>
                </span>
              ))}
            </div>
          </div>
          <div className="grid2" style={{ marginTop: 16, gap: 20 }}>
            <ShareList title="Browser" rows={devices.browser} labelWidth={64} color="var(--viz-4)" />
            <ShareList title="System" rows={devices.os} labelWidth={64} color="var(--viz-4)" />
          </div>
        </>
      )}
    </Panel>
  )
}

/** The walkthroughs' names in the app's SEO guide. */
const tourNames: Record<string, string> = {
  verify: 'Confirm you can manage the website',
  google: 'Connect Search Console and Analytics',
  baseline: 'Record the benchmark',
  audit: 'Run a website audit',
  fix: 'Fix the critical problems',
}

/** The coach in the app: who used Show me, and how far they got, plus its other helpers. */
function Coach({ coach }: { coach: NonNullable<Demographics['coach']> }) {
  const a = coach.actions
  return (
    <Panel title="Guided help" sub="People in the range who used the coach on a website">
      <div className="grid2" style={{ gap: 20 }}>
        {coach.tours.length === 0 ? (
          <p className="empty-line">Nobody has started a Show me walkthrough yet.</p>
        ) : (
          <div className="tw">
            <table className="t">
              <caption className="sr-only">Show me walkthroughs</caption>
              <thead>
                <tr>
                  <th>Show me walkthrough</th>
                  <th className="r">Started</th>
                  <th className="r">Finished</th>
                  <th className="r">Closed early</th>
                </tr>
              </thead>
              <tbody>
                {coach.tours.map((t) => (
                  <tr key={t.tour}>
                    <td>{tourNames[t.tour] ?? t.tour}</td>
                    <td className="r">{numberFmt.format(t.started)}</td>
                    <td className="r">{numberFmt.format(t.finished)}</td>
                    <td className="r">{numberFmt.format(t.closed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <BarList
          rows={[
            { key: 'explain', label: 'Opened Explain it simply', value: a.explain_opened },
            { key: 'prompt', label: 'Copied the AI assistant prompt', value: a.prompt_copied },
            { key: 'stuck', label: 'Opened Stuck? Get help', value: a.stuck_opened },
            { key: 'hidden', label: 'Hid the coach', value: a.coach_hidden },
            { key: 'expert', label: 'Switched to Expert view', value: a.view_expert },
          ]}
          format={(v) => numberFmt.format(v)}
          labelWidth={230}
          valueWidth={44}
        />
      </div>
    </Panel>
  )
}

function Cohorts({ cohorts }: { cohorts: Demographics['cohorts'] }) {
  return (
    <Panel
      title="Who keeps coming back"
      sub="Each row is a week of sign-ups; each cell is the share that did something in Arva SEO that many weeks later"
    >
      {cohorts.length === 0 ? (
        <p className="empty-line">No sign-ups in the last 12 weeks.</p>
      ) : (
        <>
          <div style={{ overflowX: 'auto', position: 'relative' }}>
            <table className="cohort">
              <thead>
                <tr>
                  <th>Signed up</th>
                  <th>People</th>
                  {Array.from({ length: 12 }, (_, i) => (
                    <th key={i}>Wk {i}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cohorts.map((c) => (
                  <tr key={c.start}>
                    <td className="lbl">{shortDate(c.start, 'week')}</td>
                    <td className="sz num">{numberFmt.format(c.size)}</td>
                    {Array.from({ length: 12 }, (_, i) => {
                      const v = c.weeks[i]
                      return v == null ? (
                        <td key={i} className="na" />
                      ) : (
                        <td
                          key={i}
                          style={{ background: sequential(v / 100), color: sequentialInk(v / 100) }}
                        >
                          {Math.round(v)}%
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: 12.5, color: 'var(--subtle)', marginTop: 10, display: 'flex', gap: 6 }}>
            <Info aria-hidden="true" style={{ width: 14, height: 14, flex: 'none', marginTop: 2 }} />
            Read across a row to follow one week’s sign-ups over time, and down a column to compare weeks at
            the same age. Week 0 is the week they signed up.
          </p>
        </>
      )}
    </Panel>
  )
}

/** The busiest day and hour of the heatmap, in words. */
function busiest(grid: number[][]): ReactNode {
  const total = grid.flat().reduce((a, b) => a + b, 0)
  if (total === 0) return 'No sign-ups in this period.'
  const byDay = grid.map((row) => row.reduce((a, b) => a + b, 0))
  const byHour = Array.from({ length: 24 }, (_, h) => grid.reduce((sum, row) => sum + (row[h] ?? 0), 0))
  const day = byDay.indexOf(Math.max(...byDay))
  const hour = byHour.indexOf(Math.max(...byHour))
  return (
    <>
      Busiest:{' '}
      <b style={{ color: 'var(--ink)' }}>
        {DAYS[day]}s, around {String(hour).padStart(2, '0')}:00
      </b>
      . Good times to post and to have someone on support.
    </>
  )
}

/** The page's figures as one CSV, a section at a time, for a spreadsheet. */
function downloadCsv(d: Demographics, range: DemographicsRange) {
  const cell = (v: string | number | null) => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const lines: (string | number | null)[][] = [
    ['Arva SEO audience', range, ...Object.entries(d.filters).map(([k, v]) => `${k}: ${v}`)],
    [],
    ['Figure', 'Value'],
    ['Users with open accounts', d.totals.open_users],
    ['New sign-ups', d.totals.new_users],
    ['Active this week', d.totals.active_7d],
    ['Activated (%)', d.totals.activation],
    ['Work email (%)', d.totals.work_email],
    ['Ticked the newsletter box (%)', d.totals.marketing],
    [],
    ['Period starting', ...CHANNEL_KEYS.map((k) => CHANNELS[k].label)],
    ...d.by_channel.map((r) => [r.start, ...CHANNEL_KEYS.map((k) => r[k])]),
    [],
    ['Country', 'Sign-ups', 'Customers', 'Pay (%)'],
    ...d.countries.rows.map((c) => [c.label, c.count, c.paid, c.paid_share]),
    [],
    ['Campaign', 'Came from', 'Sign-ups', 'Activated', 'Paid'],
    ...d.campaigns.map((c) => [c.campaign, CHANNELS[c.channel].label, c.signups, c.activated, c.paid]),
    [],
    ['Feature', 'Workspaces using it (%)'],
    ...d.adoption.map((a) => [a.label, a.share]),
  ]
  const blob = new Blob([lines.map((l) => l.map(cell).join(',')).join('\n')], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `arva-seo-audience-${range}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
